import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { noop } from 'lodash'

import NavBar from '@doordash/component-navbar'
import Separator from '@doordash/component-separator'
import {
  Text,
  Colors,
  Inset,
  Stack,
  StackChildren,
} from '@doordash/design-language'
import Button, { IconButton } from '@doordash/component-button'
import * as actions from '../../../../actions'
import {
  DeliveryIssue,
  DeliveryIssueTypes,
  PolicyViolationPages,
} from '../../../../models/types'
import { RootState } from '../../../../reducers'
import routes from '../../../../routes'
import {
  deliveryIssueDetailsSelector,
  getDasherId,
  hasSubmittedDisputeSelector,
} from '../../../../selectors/deliveryIssues'
import { SubmittedDetailsContainer } from './styles'
import { StickyTop } from '../components/styles'
import ListCell from '@doordash/component-list-cell'
import { ToastProvider, useToast } from '@doordash/component-toast'
import AdditionalInfoSection from './AdditionalInfoSection'
import DeliveryIssueCard from './DeliveryIssueCard'
import * as queryString from 'query-string'
import t from './strings'
import { StyledLoading } from '../loading'
import * as events from '../../../../events'
import { PolicyViolation, EventActions } from '../../../../typeModules/events'

type DeliveryIssueDetailsProps = {
  dasherId: string
  deliveryIssueDetails: DeliveryIssue
  getDeliveryIssueDetails: (dasherId: string, issueId: string) => void
  issueId: string
  hasSubmittedDispute: boolean
  track?: (data: PolicyViolation) => Promise<any>
}

const DisplaySubmissionToast = () => {
  const { displayToast } = useToast()
  return displayToast({
    text: t.submissionToastText(),
  })
}

/*
  We show the details of a delivery issue here by making a
  network request for the by the delivery issue ID retrieved via props.
 */
export const DeliveryIssueDetails: React.FC<DeliveryIssueDetailsProps &
  RouteComponentProps> = ({
  dasherId,
  deliveryIssueDetails,
  getDeliveryIssueDetails,
  hasSubmittedDispute,
  history,
  issueId,
  track,
}) => {
  // Fetching linking information from dispute screen.
  // If user submitted a dispute, there will be a query string attached
  // and a toast will be triggered
  const queryParams = queryString.parse(window.location.search)
  const disputeToastQuery = queryParams.submitted_dispute === 'true'
  const [disputeToast, setDisputeToast] = React.useState(disputeToastQuery)

  React.useEffect(() => {
    // Only want the toast to display once
    setDisputeToast(false)
  }, [disputeToast])

  /*
   * We can't just pass details data from the list as props because we might
   * link directly to this detail page and it'd need to fetch its own data.
   */
  React.useEffect(() => {
    // Fetch details of an issue by dasher ID and issue ID.
    // Also will mark the issue status as "read" if it's "unread".
    getDeliveryIssueDetails(dasherId, issueId)
  }, [dasherId, issueId])

  React.useEffect(() => {
    // check if the devliery issues exist, if it does,
    // fire render event. deliveryIssueDetails
    if (
      Object.keys(deliveryIssueDetails).length === 0 &&
      deliveryIssueDetails.constructor === Object
    ) {
      track({
        type: deliveryIssueDetails.issueType,
        page: PolicyViolationPages.ViolationDetail,
        action: EventActions.Rendered,
        data: {
          issueId: deliveryIssueDetails.id,
        },
      })
    }
  }, [deliveryIssueDetails])

  const isNeverDelivered =
    deliveryIssueDetails.issueType === DeliveryIssueTypes.NeverDelivered

  const storeHeadingProps = (({ createdAt, storeName }) => ({
    createdAt,
    storeName,
  }))(deliveryIssueDetails)

  // Render a loading wheel instead of a placeholder issue
  // while the api is loading.
  if (!deliveryIssueDetails.issueType) {
    return (
      <StickyTop>
        <NavBar
          onNavigationClick={() => {
            track({
              page: PolicyViolationPages.ViolationDetail,
              action: EventActions.Back,
              data: {
                name: 'loading',
                issueId: deliveryIssueDetails.id,
              },
            })
            // Go back to delivery issues list
            history.push(routes.deliveryIssueList.path)
          }}
          onOverflowClick={noop}
          textAlign={NavBar.TextAlignments.Center}
          isSeparatorVisible={false}
        />
        <StyledLoading />
      </StickyTop>
    )
  }

  return (
    <React.Fragment>
      <StickyTop>
        <NavBar
          title={
            isNeverDelivered ? t.neverDeliveredTitle() : t.extremelyLateTitle()
          }
          subtext={isNeverDelivered ? t.customerReported() : ''}
          onNavigationClick={() => {
            track({
              type: deliveryIssueDetails.issueType,
              page: PolicyViolationPages.ViolationDetail,
              action: EventActions.Back,
              data: {
                issueId: deliveryIssueDetails.id,
              },
            })
            // Go back to delivery issues list
            history.push(routes.deliveryIssueList.path)
          }}
          onOverflowClick={noop}
          textAlign={NavBar.TextAlignments.Center}
          // There is currently an issue with DLS 5.16.0 that the separator doesnt
          // span the entire width inside nav-bar. Temp using this to mitigate
          isSeparatorVisible={false}
        />
        <Separator type={Separator.Types.FullBorder} />
      </StickyTop>
      <Inset>
        <Text
          color={Colors.TextPrimary}
          styles={Text.Styles.PageDescriptionBody}
        >
          {isNeverDelivered
            ? t.neverDeliveredDescription()
            : t.extremelyLateDescription()}
        </Text>
      </Inset>
      <Stack>
        <Separator />
      </Stack>
      <ToastProvider>
        <DeliveryIssueCard
          storeHeading={storeHeadingProps}
          {...deliveryIssueDetails}
        />
        {disputeToast && <DisplaySubmissionToast />}
      </ToastProvider>
      <Separator />
      <Inset>
        <StackChildren>
          <AdditionalInfoSection isNeverDelivered={isNeverDelivered} />
          {hasSubmittedDispute ? (
            <SubmittedDetailsContainer>
              <ListCell
                iconStart={{
                  type: ListCell.IconTypes.ReceiptLine,
                  size: IconButton.Sizes.Large,
                }}
                title={
                  <Text size={Text.Sizes.S14}>
                    {t.additionalDetailsSubmittedText()}
                  </Text>
                }
                isSeparatorVisible={false}
              />
            </SubmittedDetailsContainer>
          ) : (
            <Button
              type={Button.Types.TertiaryPill}
              onClick={() => {
                track({
                  type: deliveryIssueDetails.issueType,
                  page: PolicyViolationPages.ViolationDetail,
                  action: EventActions.Clicked,
                  data: {
                    name: 'dispute',
                    issueId: deliveryIssueDetails.id,
                  },
                })
                history.push(routes.deliveryDispute.buildPath({ id: issueId }))
              }}
            >
              {t.additionalDetailsNotSubmittedText()}
            </Button>
          )}
        </StackChildren>
      </Inset>
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState) => ({
  dasherId: getDasherId(state),
  deliveryIssueDetails: deliveryIssueDetailsSelector(state),
  hasSubmittedDispute: hasSubmittedDisputeSelector(state),
})

const mapDispatchToProps = {
  getDeliveryIssueDetails: actions.getDeliveryIssueDetails,
  track: events.trackPolicyViolations,
}

export const DisconnectedDeliveryIssueDetails = DeliveryIssueDetails

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DeliveryIssueDetails)
